import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, List, Avatar, Tag, Checkbox } from "antd";
import { TableProps } from "antd/lib/table";
import { Table, TablePagination } from "components/base";
import { ROUTES } from "utils/constants";
import { useHistory } from "react-router-dom";
import { getFullName } from "utils/helpers";
import {
  UserIconCellTable,
  safetyRating,
  ActionMenu,
  assignedVehicles,
  userStatus,
} from "./cell-renders";
import { useGetUserData } from "utils/hooks";
import useDebounce from "utils/hooks";
import { parseUsersTable } from "utils/parsers";
import { dispatchUsers, setSelectedTab } from "store/actions";
import { AppState } from "store";
import { UserTabName } from "types";
import {
  setBulkActionsUser,
  setSelectedUser,
  setSelectedUserRows,
} from "store/actions/users-actions";
import { 
  UserOutlined, 
  PhoneOutlined, 
  CalendarOutlined,
  CarOutlined,
  TrophyOutlined,
  TeamOutlined
} from "@ant-design/icons";

function renderColumns(selectedTab: UserTabName, userType: number) {
  const allColumns: TableProps<any>["columns"] = [
    {
      title: "Name",
      dataIndex: "driver_name",
      render: UserIconCellTable,
      width: 140,
      ellipsis: true,
    },
    {
      title: "Phone Number",
      dataIndex: "phone_no",
      width: 100,
      ellipsis: true,
    },
    {
      title: "Role",
      dataIndex: "role",
      width: 140,
      ellipsis: true,
    },
    {
      title: "Added On",
      dataIndex: "added_on",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Added By",
      dataIndex: "added_by",
      width: 140,
      ellipsis: true,
    },
    {
      title: "Organization",
      dataIndex: "organization_name",
      width: 140,
      ellipsis: true,
    },
    {
      title: " ",
      dataIndex: "action",
      render: ActionMenu,
      align: "right",
      width: 56,
      className: "action-menu open-link --user",
    },
  ];

  const adminsColumns: TableProps<any>["columns"] = [
    {
      title: "Admin Name",
      dataIndex: "driver_name",
      render: UserIconCellTable,
      width: 120,
      ellipsis: true,
    },
    {
      title: "Phone Number",
      dataIndex: "phone_no",
      width: 120,
    },
    {
      title: "Organization",
      dataIndex: "organization_name",
      width: 140,
      ellipsis: true,
    },
    {
      title: "Added On",
      dataIndex: "added_on",
      width: 120,
    },
    {
      title: "Added By",
      dataIndex: "added_by",
      width: 120,
    },
    {
      title: " ",
      dataIndex: "action",
      render: ActionMenu,
      align: "right",
      width: 56,
      className: "action-menu open-link --user",
    },
  ];

  const ridersColumns: TableProps<any>["columns"] = [
    {
      title: "Rider Name",
      dataIndex: "driver_name",
      render: UserIconCellTable,
      width: 140,
      ellipsis: true,
    },
    {
      title: "Assigned Vehicles",
      dataIndex: "vehicle_id",
      render: assignedVehicles,
      width: 160,
      ellipsis: true,
    },
    {
      title: "Rider's Score",
      dataIndex: "rider_score",
      width: 120,
      ellipsis: true,
    },
    {
      title: "Phone Number",
      dataIndex: "phone_no",
      width: 100,
      ellipsis: true,
    },
    {
      title: "Added On",
      dataIndex: "added_on",
      width: 160,
      ellipsis: true,
    },
    {
      title: "Added By",
      dataIndex: "added_by",
      width: 140,
      ellipsis: true,
    },
    {
      title: " ",
      dataIndex: "action",
      render: ActionMenu,
      align: "right",
      width: 56,
      className: "action-menu open-link --user",
    },
  ];

  switch (selectedTab) {
    case "all":
      let allCols = allColumns;
      if (userType > 2) {
        allCols = allColumns?.filter((item) => {
          return item.title !== "Organization";
        });
      }
      return allCols;
    case "admins":
      return adminsColumns;
    case "client admins":
      return adminsColumns;
    case "riders":
      return ridersColumns;
    default:
      return allColumns;
  }
}

// Create a separate component for the mobile card
interface MobileUserCardProps {
  item: any;
  selectedTab: UserTabName;
  userType: number;
  onRowClick: (item: any) => void;
}

const MobileUserCard: React.FC<MobileUserCardProps> = ({
  item,
  selectedTab,
  userType,
  onRowClick,
}) => {
  const getRoleColor = (role: string) => {
    const roleLower = role?.toLowerCase() || '';
    if (roleLower.includes('admin')) return 'purple';
    if (roleLower.includes('rider')) return 'blue';
    if (roleLower.includes('driver')) return 'green';
    if (roleLower.includes('monitor')) return 'orange';
    return 'default';
  };

  return (
    <Card
      className="mobile-user-card"
      onClick={() => onRowClick(item)}
      hoverable
    >
      {/* Header Section */}
      <div className="card-header">
        <div className="user-avatar-wrapper">
          <Avatar
            src={item.raw?.profilePhotoLink}
            icon={<UserOutlined />}
            size={48}
            className="user-avatar"
          />
        </div>
        <div className="user-main-info">
          <h4 className="user-name">{item.driver_name || "N/A"}</h4>
          {item.role && (
            <Tag color={getRoleColor(item.role)} className="user-role-tag">
              {item.role}
            </Tag>
          )}
        </div>
        <div 
          className="card-action-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          {ActionMenu(item.action, item)}
        </div>
      </div>

      {/* Body Section */}
      <div className="card-body">
        {/* Phone Number */}
        {item.phone_no && (
          <div className="info-row">
            <span className="info-label">
              <PhoneOutlined className="info-icon" />
              Phone
            </span>
            <span className="info-value">{item.phone_no}</span>
          </div>
        )}

        {/* Vehicle Assignment (for riders) */}
        {selectedTab === "riders" && item.vehicle_id && (
          <div className="info-row">
            <span className="info-label">
              <CarOutlined className="info-icon" />
              Vehicle
            </span>
            <span className="info-value">{item.vehicle_id}</span>
          </div>
        )}

        {/* Rider Score */}
        {selectedTab === "riders" && item.rider_score && (
          <div className="info-row">
            <span className="info-label">
              <TrophyOutlined className="info-icon" />
              Score
            </span>
            <span className="info-value score-value">{item.rider_score}</span>
          </div>
        )}

        {/* Organization */}
        {item.organization_name && userType <= 2 && (
          <div className="info-row">
            <span className="info-label">
              <TeamOutlined className="info-icon" />
              Organization
            </span>
            <span className="info-value">{item.organization_name}</span>
          </div>
        )}

        {/* Added On */}
        {item.added_on && (
          <div className="info-row">
            <span className="info-label">
              <CalendarOutlined className="info-icon" />
              Added On
            </span>
            <span className="info-value">{item.added_on}</span>
          </div>
        )}

        {/* Added By */}
        {item.added_by && (
          <div className="info-row">
            <span className="info-label">
              <UserOutlined className="info-icon" />
              Added By
            </span>
            <span className="info-value">{item.added_by}</span>
          </div>
        )}
      </div>
    </Card>
  );
};

export default function TableView() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  const { selectedTab, search, selectedUserRows } = useSelector<
    AppState,
    AppState["users"]
  >((state) => state.users);
  const {
    common: { userType = 1 },
  } = useSelector<AppState, AppState>((state) => state);

  // Detect screen size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onChangeSelection = useCallback(
    (value) => {
      dispatch(setSelectedUserRows(value));

      if (value.length !== 0) {
        dispatch(setBulkActionsUser(true));
      } else {
        dispatch(setBulkActionsUser(false));
      }
    },
    [dispatch]
  );

  const { page, onNext, onBack, tableData, resetCallback } =
    useGetUserData(selectedTab);
  const dataSource = useMemo(() => {
    return parseUsersTable(search, tableData);
  }, [search, tableData]);
  const debounceSearchString = useDebounce(search, 1000);

  useEffect(() => {
    if (userType == 6) {
      dispatch(setSelectedTab("operations monitor"));
    }
    dispatchUsers(dispatch, selectedTab);
  }, [dispatch, selectedTab, page.selectedPage, debounceSearchString]);

  const handleRowClick = useCallback(
    (user: any) => {
      console.log("clicked User : ", user?.raw)
      dispatch(
        setSelectedUser({
          id: user?.raw.id,
          name: getFullName(user?.raw.firstName, user?.raw.lastName),
          phone: user?.raw.phoneNumber,
          email: user?.raw.email,
          imgUrl: user?.raw.profilePhotoLink,
          type: user?.raw.type,
          documentId: user?.raw.documentId,
          nationalId: user?.raw.nationalId,
          lincenceNo: user?.raw.licenseNo,
          document: user?.raw.document,
          documentName: user?.raw.documentName,
          documentType: user?.raw.documentExt,
          organization_id: user?.raw.organizationId,
          organization_name: user?.raw.organizationName,
          vehicle: user?.raw.vehicle,
          user_business_type: user?.raw?.userbusinesstype
        })
      );
      history.push(`${ROUTES.user_detail}/${user.raw.id}`);
    },
    [dispatch, history]
  );

  // Mobile Card View
  if (isMobile) {
    return (
      <div className="mobile-table-view users-mobile-view">
        <div className="mobile-cards-list">
          {dataSource && dataSource.length > 0 ? (
            dataSource.map((item: any) => (
              <MobileUserCard
                key={item.key || item.raw?.id}
                item={item}
                selectedTab={selectedTab}
                userType={userType}
                onRowClick={handleRowClick}
              />
            ))
          ) : (
            <div className="no-data-message">
              <UserOutlined className="no-data-icon" />
              <p>No users found</p>
            </div>
          )}
        </div>
        <div className="pagination-wrapper">
          <TablePagination
            page={page}
            onBack={onBack}
            onNext={onNext}
            resetCallback={resetCallback}
          />
        </div>
      </div>
    );
  }

  // Tablet view - show fewer columns
  const columns = isTablet
    ? renderColumns(selectedTab, userType)?.filter((col) => {
        // Hide less important columns on tablet
        return !["Added By", "Organization"].includes(col.title as string);
      })
    : renderColumns(selectedTab, userType);

  // Desktop/Tablet Table View
  return (
    <>
      <Table
        customClasses="user-table"
        columns={columns}
        dataSource={dataSource}
        onRow={(record) => {
          return {
            onClick: () => handleRowClick(record),
          };
        }}
        pagination={false}
      //  scroll={{ y: "65vh", x: isTablet ? 800 : undefined }}
      />
      <div className="pagination-wrapper">
        <TablePagination
          page={page}
          onBack={onBack}
          onNext={onNext}
          resetCallback={resetCallback}
        />
      </div>
    </>
  );
}